'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useToast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/organization-context';
import { apiClient } from '@/lib/api-client';
import {
  PetrohqApiResponseListTimesaleAggregationDto,
  TimesaleAggregationDto,
  PetrohqApiResponseListTimesaleOilChartDto,
  PetrohqApiResponseListTimesaleSaleTypeChartDto,
  PetrohqApiResponseListTimesalePumpChartDto,
  PetrohqApiResponseListTimesaleTankChartDto,
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import Highcharts from 'highcharts';
import { Download } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

export function TimeSale() {
  const { toast } = useToast();
  const { getAllStores } = useOrganization();
  // 온디맨드 방식: API 호출 시점에만 필터 상태 읽기

  // 상태 관리
  const [timeSaleData, setTimeSaleData] = useState<TimesaleAggregationDto[]>(
    []
  );
  const [chartData, setChartData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // 차트 관련 상태
  const [chartType, setChartType] = useState<
    'oil' | 'sale-type' | 'pump' | 'tank'
  >('oil');
  const [chartViewType, setChartViewType] = useState<
    'HOURLY' | 'DAILY' | 'MONTHLY' | 'DAY_OF_WEEK' | 'YEARLY'
  >('HOURLY');
  const chartsRef = useRef<Map<string, Highcharts.Chart>>(new Map());

  // 판매자료 데이터 가져오기 함수
  const fetchTimeSaleData = useCallback(async () => {
    setIsLoading(true);
    try {
      // API 호출 시점에 현재 필터 상태 읽기
      const filters = useFilterStore.getState();

      const storeCodeList =
        filters.store.length > 0 ? filters.store : getAllStores();

      // 중앙집중식 파라미터 구축 사용
      const params = filters.buildApiParams('timesale', storeCodeList);

      const response = await apiClient.get('sales/timesale/aggregation', {
        searchParams: params,
      });
      const data =
        (await response.json()) as PetrohqApiResponseListTimesaleAggregationDto;

      if (data.result) {
        setTimeSaleData(data.result);
      }
    } catch (error) {
      console.error('TimeSale data fetch error:', error);
      toast({
        title: '조회 실패',
        description: '판매자료 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [getAllStores, toast]);

  // 차트 데이터 가져오기 함수
  const fetchChartData = useCallback(async () => {
    // API 호출 시점에 현재 필터 상태 읽기
    const filters = useFilterStore.getState();

    try {
      const storeCodeList =
        filters.store.length > 0 ? filters.store : getAllStores();

      const params = new URLSearchParams();

      storeCodeList.forEach((storeCode: string) => {
        params.append('storeCodeList', storeCode);
      });

      if (filters.dateRange?.start && filters.dateRange?.end) {
        params.append('startDate', filters.dateRange.start.replace(/-/g, ''));
        params.append('endDate', filters.dateRange.end.replace(/-/g, ''));
      }

      params.append('chartViewType', chartViewType);

      const response = await apiClient.get(
        `sales/timesale/chart/${chartType}`,
        { searchParams: params }
      );
      let data: any;

      // 차트 타입별 응답 타입 처리
      switch (chartType) {
        case 'oil':
          data =
            (await response.json()) as PetrohqApiResponseListTimesaleOilChartDto;
          break;
        case 'sale-type':
          data =
            (await response.json()) as PetrohqApiResponseListTimesaleSaleTypeChartDto;
          break;
        case 'pump':
          data =
            (await response.json()) as PetrohqApiResponseListTimesalePumpChartDto;
          break;
        case 'tank':
          data =
            (await response.json()) as PetrohqApiResponseListTimesaleTankChartDto;
          break;
        default:
          data = await response.json();
      }

      if (data.result) {
        setChartData(data.result);
      }
    } catch (error) {
      console.error('Chart data fetch error:', error);
      toast({
        title: '차트 조회 실패',
        description: '차트 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    }
  }, [chartType, chartViewType, getAllStores, toast]);

  // 조회 버튼 클릭 핸들러
  const handleSearch = () => {
    fetchTimeSaleData();
    fetchChartData();
  };

  // 초기 데이터 로드
  useEffect(() => {
    fetchTimeSaleData();
    fetchChartData();
  }, []);

  // 차트 데이터 변경시 차트 생성
  useEffect(() => {
    if (chartData.length > 0) {
      createCharts(chartData);
    }
  }, [chartData, chartType, chartViewType]);

  // 차트 데이터 fetch
  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  // 차트 생성 함수
  const createCharts = (data: any[]) => {
    if (!data || data.length === 0) return;

    // 기존 차트 정리
    chartsRef.current.forEach(chart => chart.destroy());
    chartsRef.current.clear();

    // 매장별로 차트 생성
    const stores = [...new Set(data.map(x => x.storeCd))];

    stores.forEach(storeCd => {
      const storeData = data.filter(x => x.storeCd === storeCd);
      const storeName = storeData[0]?.storeName || storeCd;

      setTimeout(() => {
        const chartContainer = document.getElementById(`chart-${storeCd}`);
        if (chartContainer) {
          createSingleChart(`chart-${storeCd}`, storeData, storeName);
        }
      }, 100);
    });
  };

  // 개별 차트 생성 함수
  const createSingleChart = (
    chartId: string,
    data: any[],
    storeName: string
  ) => {
    const getXAxisValues = () => {
      switch (chartViewType) {
        case 'HOURLY':
          return Array.from({ length: 24 }, (_, i) => i);
        case 'DAILY':
          return Array.from({ length: 31 }, (_, i) => i + 1);
        case 'MONTHLY':
          return Array.from({ length: 12 }, (_, i) => i + 1);
        case 'DAY_OF_WEEK':
          return ['일', '월', '화', '수', '목', '금', '토'];
        case 'YEARLY':
          const years = [...new Set(data.map(x => x.groupField))];
          return years.sort();
        default:
          return [];
      }
    };

    const xAxisValues = getXAxisValues();
    const seriesData: any[] = [];

    if (chartType === 'oil') {
      // 유종별 차트
      const oilTypes = [...new Set(data.map(x => x.oilType))];

      oilTypes.forEach(oilType => {
        const oilData = data.filter(x => x.oilType === oilType);
        const oilName = oilData[0]?.oilName || oilType;

        const amtPoints = xAxisValues.map((value, index) => {
          const point = oilData.find(x => x.groupField == value);
          return { x: index, y: point?.amt || 0 };
        });

        const qtyPoints = xAxisValues.map((value, index) => {
          const point = oilData.find(x => x.groupField == value);
          return { x: index, y: point?.qty || 0 };
        });

        const cntPoints = xAxisValues.map((value, index) => {
          const point = oilData.find(x => x.groupField == value);
          return { x: index, y: point?.cnt || 0 };
        });

        seriesData.push(
          {
            name: `${oilName} 금액`,
            type: 'column',
            data: amtPoints,
            tooltip: { valueSuffix: ' ₩' },
          },
          {
            name: `${oilName} 수량`,
            type: 'spline',
            yAxis: 1,
            data: qtyPoints,
          },
          {
            name: `${oilName} 건수`,
            type: 'spline',
            yAxis: 2,
            data: cntPoints,
            dashStyle: 'shortdot',
            tooltip: { valueSuffix: ' 건' },
          }
        );
      });
    } else if (chartType === 'sale-type') {
      // 판매유형별 차트
      const saleTypes = [...new Set(data.map(x => x.saleType))];

      saleTypes.forEach(saleType => {
        const saleTypeData = data.filter(x => x.saleType === saleType);
        const saleTypeName = saleTypeData[0]?.saleTypeName || saleType;

        const amtPoints = xAxisValues.map((value, index) => {
          const point = saleTypeData.find(x => x.groupField == value);
          return { x: index, y: point?.amt || 0 };
        });

        const qtyPoints = xAxisValues.map((value, index) => {
          const point = saleTypeData.find(x => x.groupField == value);
          return { x: index, y: point?.qty || 0 };
        });

        const cntPoints = xAxisValues.map((value, index) => {
          const point = saleTypeData.find(x => x.groupField == value);
          return { x: index, y: point?.cnt || 0 };
        });

        seriesData.push(
          {
            name: `${saleTypeName} 금액`,
            type: 'column',
            data: amtPoints,
            tooltip: { valueSuffix: ' ₩' },
          },
          {
            name: `${saleTypeName} 수량`,
            type: 'spline',
            yAxis: 1,
            data: qtyPoints,
          },
          {
            name: `${saleTypeName} 건수`,
            type: 'spline',
            yAxis: 2,
            data: cntPoints,
            dashStyle: 'shortdot',
            tooltip: { valueSuffix: ' 건' },
          }
        );
      });
    } else if (chartType === 'pump') {
      // 주유기별 차트
      const pumpNos = [...new Set(data.map(x => x.pumpNo))].sort(
        (a, b) => parseInt(a) - parseInt(b)
      );

      const amtPoints = pumpNos.map((pumpNo, index) => {
        const point = data.find(x => x.pumpNo === pumpNo);
        return { x: index, y: point?.amt || 0 };
      });

      const qtyPoints = pumpNos.map((pumpNo, index) => {
        const point = data.find(x => x.pumpNo === pumpNo);
        return { x: index, y: point?.qty || 0 };
      });

      const cntPoints = pumpNos.map((pumpNo, index) => {
        const point = data.find(x => x.pumpNo === pumpNo);
        return { x: index, y: point?.cnt || 0 };
      });

      seriesData.push(
        {
          name: '금액',
          type: 'column',
          data: amtPoints,
          tooltip: { valueSuffix: ' ₩' },
        },
        {
          name: '수량',
          type: 'spline',
          yAxis: 1,
          data: qtyPoints,
        },
        {
          name: '건수',
          type: 'spline',
          yAxis: 2,
          data: cntPoints,
          dashStyle: 'shortdot',
          tooltip: { valueSuffix: ' 건' },
        }
      );

      // 주유기별 차트의 경우 xAxis 카테고리를 주유기 번호로 설정
      xAxisValues.length = 0;
      xAxisValues.push(...pumpNos);
    } else if (chartType === 'tank') {
      // 탱크별 차트
      const tankCodes = [...new Set(data.map(x => x.tkCode))].sort(
        (a, b) => parseInt(a) - parseInt(b)
      );

      const amtPoints = tankCodes.map((tkCode, index) => {
        const point = data.find(x => x.tkCode === tkCode);
        return { x: index, y: point?.amt || 0 };
      });

      const qtyPoints = tankCodes.map((tkCode, index) => {
        const point = data.find(x => x.tkCode === tkCode);
        return { x: index, y: point?.qty || 0 };
      });

      const cntPoints = tankCodes.map((tkCode, index) => {
        const point = data.find(x => x.tkCode === tkCode);
        return { x: index, y: point?.cnt || 0 };
      });

      seriesData.push(
        {
          name: '금액',
          type: 'column',
          data: amtPoints,
          tooltip: { valueSuffix: ' ₩' },
        },
        {
          name: '수량',
          type: 'spline',
          yAxis: 1,
          data: qtyPoints,
        },
        {
          name: '건수',
          type: 'spline',
          yAxis: 2,
          data: cntPoints,
          dashStyle: 'shortdot',
          tooltip: { valueSuffix: ' 건' },
        }
      );

      // 탱크별 차트의 경우 xAxis 카테고리를 탱크 코드로 설정
      xAxisValues.length = 0;
      xAxisValues.push(...tankCodes);
    }

    const chart = Highcharts.chart(chartId, {
      chart: { zooming: { type: 'xy' } },
      title: { text: `${storeName} Chart` },
      xAxis: [
        {
          categories: xAxisValues,
          crosshair: true,
        },
      ],
      yAxis: [
        {
          gridLineWidth: 0,
          title: { text: '금액', rotation: 0 },
          labels: { format: '{value} ₩' },
        },
        {
          title: { text: '수량', rotation: 0 },
          labels: { format: '{value}' },
          opposite: true,
        },
        {
          gridLineWidth: 0,
          title: { text: '건수', rotation: 0 },
          labels: { format: '{value} 건' },
          opposite: true,
        },
      ],
      tooltip: { shared: true },
      legend: {
        layout: 'vertical',
        align: 'right',
        verticalAlign: 'middle',
      },
      series: seriesData,
      credits: { enabled: false },
    });

    chartsRef.current.set(chartId, chart);
  };

  // 차트 옵션 변경 핸들러
  const handleChartOptionChange = (type: string, viewType?: string) => {
    setChartType(type as any);
    if (viewType) {
      setChartViewType(viewType as any);
    }
    fetchChartData();
  };

  // 엑셀 내보내기
  const handleExcelExport = () => {
    // RealGridReact export 기능 구현 필요
    toast({
      title: '준비 중',
      description: '엑셀 내보내기 기능을 준비하고 있습니다',
    });
  };

  // 매장별 차트 컨테이너 렌더링
  const renderChartContainers = () => {
    if (!chartData || chartData.length === 0) return null;

    const stores = [...new Set(chartData.map((x: any) => x.storeCd))];

    return stores.map(storeCd => {
      const storeName =
        chartData.find((x: any) => x.storeCd === storeCd)?.storeName || storeCd;

      return (
        <ExpandableCard key={storeCd} title={`${storeName} Chart`}>
          <div id={`chart-${storeCd}`} className='w-full h-[400px]' />
        </ExpandableCard>
      );
    });
  };

  return (
    <div className='space-y-4'>
      {/* 메인 그리드 */}
      <ExpandableCard
        title='판매자료 집계'
        breadcrumb={
          <Breadcrumb items={menuBreadcrumbItems('timesale')} />
        }
        pageHeader={
          <PageHeader
            onSearch={handleSearch}
            isLoading={isLoading}
            actions={
              <Button variant='outline' size='sm' onClick={handleExcelExport}>
                <Download className='h-4 w-4 mr-2' />
                엑셀
              </Button>
            }
          />
        }
        onExpandChange={setIsExpanded}
      >
        <div
          className={`border rounded-lg ${isExpanded ? 'h-full' : 'h-[230px]'}`}
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                display: { fitStyle: RealGrid.GridFitStyle.FILL },
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                footer: { visible: true },
              }}
              rows={timeSaleData}
            >
              <RGDataField
                fieldName={'compCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'compName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilType'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'drum'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'qty'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'amount'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'cnt'}
                dataType={RealGrid.ValueType.NUMBER}
              />

              <RGDataColumn
                fieldName={'storeName'}
                header={{ text: '매장' }}
                width={120}
              />
              <RGDataColumn
                fieldName={'oilName'}
                header={{ text: '유종' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'drum'}
                header={{ text: '드럼' }}
                width={80}
                numberFormat={'#,##0.0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'qty'}
                header={{ text: '수량' }}
                width={100}
                numberFormat={'#,##0.000'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'amount'}
                header={{ text: '금액' }}
                width={120}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'cnt'}
                header={{ text: '건수' }}
                width={80}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
            </RealGridReact>
          </div>
        </div>

        <div className='mt-4 flex items-center justify-between'>
          <div className='flex items-center space-x-4'>
            <span className='text-sm text-muted-foreground'>
              총 {timeSaleData.length}건
            </span>
            {isLoading && (
              <span className='text-sm text-blue-600'>로딩 중...</span>
            )}
          </div>
        </div>
      </ExpandableCard>

      {/* 차트 영역 - 조회 후 차트가 있을 때만 집계 변경 버튼을 함께 보여준다 */}
      {chartData.length > 0 && (
        <div className='space-y-4'>
          <div className='flex justify-end'>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='outline' size='sm'>
                  차트 집계 변경
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>유종</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem
                      onClick={() => handleChartOptionChange('oil', 'HOURLY')}
                    >
                      시간별
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleChartOptionChange('oil', 'DAILY')}
                    >
                      일별
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleChartOptionChange('oil', 'MONTHLY')}
                    >
                      월별
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        handleChartOptionChange('oil', 'DAY_OF_WEEK')
                      }
                    >
                      요일별
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => handleChartOptionChange('oil', 'YEARLY')}
                    >
                      연도별
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>판매유형</DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem
                      onClick={() =>
                        handleChartOptionChange('sale-type', 'HOURLY')
                      }
                    >
                      시간별
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        handleChartOptionChange('sale-type', 'DAILY')
                      }
                    >
                      일별
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        handleChartOptionChange('sale-type', 'MONTHLY')
                      }
                    >
                      월별
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        handleChartOptionChange('sale-type', 'DAY_OF_WEEK')
                      }
                    >
                      요일별
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() =>
                        handleChartOptionChange('sale-type', 'YEARLY')
                      }
                    >
                      연도별
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuItem onClick={() => handleChartOptionChange('pump')}>
                  주유기
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleChartOptionChange('tank')}>
                  탱크
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {renderChartContainers()}
        </div>
      )}
    </div>
  );
}
